import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { getAuthSession } from "@/api/auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatMoney } from "@/lib/currency";
import { applyPromoDiscount } from "@/lib/promo";
import type { ParticipationPaymentStatus } from "@/api/payments";
import { useEventPaymentInfo } from "@/hooks/useLocationPayment";
import { HostPaymentBlock } from "./HostPaymentBlock";

const CHECKOUT_URL = "/api/checkout";
const REDEEM_FREE_URL = "/api/redeem-free";

/** A participation member the payer may cover. Passed in by the caller (which
 *  already has the team roster) so the dialog needn't re-fetch. */
export interface PayMember {
  person_id: number;
  first_name: string | null;
  last_name: string | null;
  is_free: boolean;
  payment_status: ParticipationPaymentStatus;
}

// Per-event contribution tiers. Independent from the Support-page SUPPORT_TIERS
// (different copy + amounts) — keep the two separate on purpose.
const EVENT_TIERS: readonly { emoji: string; name: string; amount: number; desc: string; recommended?: boolean }[] = [
  { emoji: "🏃", name: "Starter", amount: 500, desc: "Buy us a post-run coffee" },
  { emoji: "⭐", name: "Standard", amount: 1000, desc: "Covers your questions & materials" },
  { emoji: "🏆", name: "Supporter", amount: 1500, desc: "Keeps a whole event running", recommended: true },
  { emoji: "❤️", name: "Champion", amount: 2000, desc: "Funds the next event" },
];

/**
 * Modal for event contributions. Two modes share one flow (both post
 * type:"event" so the payment is tracked for the participant):
 *   - "pay":     Pay Now — the event price is the suggested default, but the
 *                participant may pay any amount.
 *   - "support": Support Project — optional donation for first-timers.
 *
 * Shared by the upcoming-events cards and the past-events table.
 */
export function PayDialog({
  open,
  onOpenChange,
  mode,
  eventId,
  participationId,
  priceCents,
  priceCurrency,
  members,
  onPaid,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: "pay" | "support";
  eventId: number;
  participationId: number;
  priceCents: number | null;
  priceCurrency?: string | null;
  /** Team roster for member selection. Omitted/≤1 member => solo single-pay. */
  members?: PayMember[];
  /** Called after a successful free-promo redeem so the parent can refetch the
   *  member's new free state (the free flow has no Stripe redirect to trigger it). */
  onPaid?: () => void;
}) {
  const { user, displayName, personId } = useAuth();
  const { toast } = useToast();

  // A priced 'pay' event is fixed-price: the amount + currency are enforced
  // server-side, so the dialog just shows the price and a single Pay button
  // (no tiers / custom amount). Support mode and price-less events keep the
  // pay-what-you-want tier picker.
  const isFixedPrice = mode === "pay" && !!priceCents && priceCents > 0;
  const ccy = (priceCurrency || "gbp").toLowerCase();

  // Default suggestion: in "pay" mode the event price leads; otherwise the
  // recommended tier (Supporter). Tiers themselves always render ascending.
  const defaultAmount = useMemo(() => {
    if (mode === "pay" && priceCents && priceCents > 0) return priceCents;
    return EVENT_TIERS.find((t) => t.recommended)?.amount ?? EVENT_TIERS[0].amount;
  }, [mode, priceCents]);

  const [selected, setSelected] = useState<number | null>(defaultAmount);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  // Promo code (fixed-price events only). `promo` holds the validated
  // discount so the button + preview reflect the real charge. Validation is a
  // live preview; checkout.js re-validates and recomputes authoritatively.
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState<{ type: string; value: number | null } | null>(null);
  const [promoState, setPromoState] = useState<"idle" | "checking" | "invalid">("idle");

  // Team member selection (fixed-price teams only). A member is payable when
  // not comped and not already paid; those are pre-ticked. Paid/free members
  // show disabled. Solo (≤1 member) skips the checklist entirely.
  const payableMembers = useMemo(
    () => (members ?? []).filter((m) => !m.is_free && m.payment_status !== "paid"),
    [members],
  );
  const isTeamPay = isFixedPrice && (members?.length ?? 0) > 1;
  const [checked, setChecked] = useState<Set<number>>(new Set());

  // Pre-tick every unpaid member when the dialog opens (the common case is the
  // payer settling the whole team). Runs on open + roster change.
  useEffect(() => {
    if (!open) return;
    setChecked(new Set(payableMembers.map((m) => m.person_id)));
  }, [open, payableMembers]);

  const selectedCount = isTeamPay ? checked.size : 1;

  // Reset selection whenever the dialog (re)opens. If the default doesn't match
  // a tier (e.g. an odd event price), prefill the custom field instead.
  useEffect(() => {
    if (!open) return;
    if (EVENT_TIERS.some((t) => t.amount === defaultAmount)) {
      setSelected(defaultAmount);
      setCustom("");
    } else {
      setSelected(null);
      setCustom(String(defaultAmount / 100));
    }
  }, [open, defaultAmount]);

  // Clear promo state whenever the dialog reopens.
  useEffect(() => {
    if (!open) {
      setPromoInput("");
      setPromo(null);
      setPromoState("idle");
    }
  }, [open]);

  // Debounced live validation of the promo code (fixed-price only).
  useEffect(() => {
    if (!isFixedPrice) return;
    const code = promoInput.trim();
    if (!code) {
      setPromo(null);
      setPromoState("idle");
      return;
    }
    setPromoState("checking");
    // Guards against an older in-flight request resolving after a newer one and
    // overwriting state with a stale verdict for a code the user has moved on from.
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await fetch("/api/promo-validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, event_id: eventId }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (data.valid) {
          setPromo({ type: data.type, value: data.value });
          setPromoState("idle");
        } else {
          setPromo(null);
          setPromoState("invalid");
        }
      } catch {
        if (cancelled) return;
        setPromo(null);
        setPromoState("invalid");
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [promoInput, isFixedPrice, eventId]);

  // Discounted charge preview mirrors the server's applyPromoDiscount, applied
  // to the whole basket (per-ticket price × selected members).
  const basketPrice = (priceCents ?? 0) * selectedCount;
  const discountedPrice = useMemo(
    () => (isFixedPrice ? applyPromoDiscount(basketPrice, promo) : priceCents ?? 0),
    [isFixedPrice, basketPrice, promo, priceCents],
  );

  const customPounds = parseFloat(custom);
  const customValid = Number.isFinite(customPounds) && customPounds >= 1;
  // Fixed-price events charge the stored price × members (less any valid promo);
  // the tier/custom selection only drives the pay-what-you-want path.
  const amount = isFixedPrice ? discountedPrice : (selected ?? (customValid ? Math.round(customPounds * 100) : null));
  // A team pay needs at least one member selected.
  const canPay = !isTeamPay || selectedCount > 0;

  // A free promo settles via /api/redeem-free (no Stripe — a £0 basket has no
  // checkout path), flipping is_free instead of taking a payment.
  const isFreePromo = isFixedPrice && promo?.type === "free";

  // Host-run locations with their own payment details: the participant pays
  // the host (honouring any promo), not our Stripe checkout. A zero total keeps
  // the free paths below. Unpriced events pay the host too, with no amount.
  const { data: hostPay, isLoading: hostPayLoading } = useEventPaymentInfo(eventId, open && mode === "pay");
  // Gate on mode too: a disabled query still returns cached data, and donations
  // ("support" mode) must keep coming to us.
  const payHost =
    mode === "pay" && hostPay && !isFreePromo && canPay && (!isFixedPrice || !!amount) ? hostPay : null;

  const handlePay = async () => {
    if (!amount || !canPay) return;
    if (!personId) {
      toast({ title: "Setting up your account — one moment, then try again", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Support donations go through the event flow too, so they land
          // attached to the event and person like any other payment. A person
          // may have several payments for one event; the read side sums them.
          type: "event",
          amount,
          event_id: eventId,
          participation_id: participationId,
          personId,
          ...(isTeamPay ? { person_ids: [...checked] } : {}),
          email: user?.email,
          name: displayName,
          ...(isFixedPrice && promo ? { promoCode: promoInput.trim() } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        toast({ title: data.error || "Payment failed", variant: "destructive" });
        return;
      }
      window.location.href = data.url;
    } catch {
      toast({ title: "Something went wrong — please try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemFree = async () => {
    if (!canPay) return;
    if (!personId) {
      toast({ title: "Setting up your account — one moment, then try again", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      // The endpoint requires a real access token — it grants free entry, so an
      // allowed Origin alone isn't enough to prove who is asking.
      const {
        data: { session },
      } = await getAuthSession();
      if (!session) {
        toast({ title: "Session expired — sign in and try again", variant: "destructive" });
        return;
      }
      const res = await fetch(REDEEM_FREE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          code: promoInput.trim(),
          event_id: eventId,
          participation_id: participationId,
          person_ids: isTeamPay ? [...checked] : [personId],
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        toast({ title: data.error || "Couldn't redeem that code", variant: "destructive" });
        return;
      }
      toast({ title: "You're in — free entry confirmed 🎉" });
      onOpenChange(false);
      // The free flow has no Stripe redirect to refresh the parent, so ask it to
      // refetch the member's new free state.
      onPaid?.();
    } catch {
      toast({ title: "Something went wrong — please try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{mode === "pay" ? "Pay for your entry" : "Support the project"}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {isFixedPrice
              ? "Your ticket for this event."
              : mode === "pay"
                ? payHost
                  ? "Pay whatever works for you."
                  : "Suggested amount below — pay whatever works for you."
                : "Your entry is free — make an optional donation if you'd like to support Quizrun."}
          </p>
        </DialogHeader>

        {isFixedPrice ? (
          <div className="space-y-3 mt-1">
            {isTeamPay && (
              <div className="space-y-1.5">
                <p className="text-sm font-medium text-foreground">Who are you paying for?</p>
                <div className="rounded-lg border divide-y">
                  {members!.map((m) => {
                    const name = [m.first_name, m.last_name].filter(Boolean).join(" ") || "Member";
                    const alreadyPaid = m.payment_status === "paid";
                    const disabled = alreadyPaid || m.is_free;
                    return (
                      <label
                        key={m.person_id}
                        className={`flex items-center gap-2.5 px-3 py-2 text-sm ${
                          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={checked.has(m.person_id)}
                          onChange={(e) =>
                            setChecked((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(m.person_id);
                              else next.delete(m.person_id);
                              return next;
                            })
                          }
                          className="h-4 w-4 accent-primary"
                        />
                        <span className="flex-1 min-w-0 truncate text-foreground">{name}</span>
                        {alreadyPaid && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
                            Paid
                          </span>
                        )}
                        {!alreadyPaid && m.is_free && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                            Free
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="flex items-baseline justify-between rounded-lg border p-4">
              <span className="text-sm font-medium text-foreground">
                {isTeamPay ? `${selectedCount} × ${formatMoney(priceCents!, ccy)}` : "Ticket price"}
              </span>
              {promo && discountedPrice !== basketPrice ? (
                <span className="flex items-baseline gap-2">
                  <span className="text-sm text-muted-foreground line-through">{formatMoney(basketPrice, ccy)}</span>
                  <span className="text-2xl font-black text-primary">{formatMoney(discountedPrice, ccy)}</span>
                </span>
              ) : (
                <span className="text-2xl font-black text-primary">{formatMoney(basketPrice, ccy)}</span>
              )}
            </div>
            <div>
              <input
                type="text"
                placeholder="Promo code"
                aria-label="Promo code"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border bg-card text-sm uppercase placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              {promoState === "checking" && (
                <p className="text-xs text-muted-foreground mt-1">Checking…</p>
              )}
              {promoState === "invalid" && (
                <p className="text-xs text-destructive mt-1">That code isn't valid.</p>
              )}
              {promo && (
                <p className="text-xs text-primary mt-1">
                  {promo.type === "free" ? "Free entry applied 🎉" : `${promo.value}% off applied 🎉`}
                </p>
              )}
            </div>
          </div>
        ) : payHost ? null : (
        <>
        <div className="grid gap-2 mt-1">
          {EVENT_TIERS.map((tier) => (
            <button
              key={tier.name}
              type="button"
              onClick={() => { setSelected(tier.amount); setCustom(""); }}
              className={`relative flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                selected === tier.amount
                  ? "border-primary bg-primary/10 ring-1 ring-primary/20"
                  : "border-border bg-card hover:bg-accent/50"
              }`}
            >
              {tier.recommended && (
                <span className="absolute -top-2 right-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold leading-none text-primary-foreground shadow-sm">
                  Most popular
                </span>
              )}
              <span className="text-2xl" aria-hidden="true">{tier.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-foreground">{tier.name}</div>
                <div className="text-xs text-muted-foreground">{tier.desc}</div>
              </div>
              <div className="text-base font-black text-primary shrink-0">{formatMoney(tier.amount, "gbp")}</div>
            </button>
          ))}
        </div>

        <div className="relative mt-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
          <input
            type="number"
            min="1"
            step="1"
            placeholder="Other amount"
            aria-label="Custom amount in pounds"
            value={custom}
            onChange={(e) => { setCustom(e.target.value); setSelected(null); }}
            onFocus={() => setSelected(null)}
            className="w-full h-10 pl-7 pr-3 rounded-lg border bg-card text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        </>
        )}

        {payHost ? (
          <div className="mt-2 space-y-2">
            <p className="text-sm text-muted-foreground">This event is run by a local host &mdash; please pay them directly.</p>
            <HostPaymentBlock payment={payHost} amountLabel={isFixedPrice && amount ? formatMoney(amount, ccy) : null} />
          </div>
        ) : (
        <>
        <Button
          className="w-full mt-2"
          onClick={isFreePromo ? handleRedeemFree : handlePay}
          disabled={loading || hostPayLoading || (!isFreePromo && (!amount || (isFixedPrice && amount === 0))) || !canPay}
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          {isTeamPay && selectedCount === 0
            ? "Select a member"
            : isFreePromo
              ? "Get free entry"
              : isFixedPrice && amount === 0
                ? "Free — nothing to pay"
                : amount
                  ? `Pay ${formatMoney(amount, isFixedPrice ? ccy : "gbp")}`
                  : "Choose amount"}
        </Button>

        <p className="text-center text-[11px] text-muted-foreground">
          Powered by Stripe · Secure payment
        </p>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
}
